/**
 * Tela de configurações do usuário.
 * Opções: solicitar pagamento da taxa ao receber; slider de absorção e fixar no Receber; QR Code da carteira ativa.
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings, ArrowLeft, QrCode, Copy, Lock, Unlock } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useSettings } from '@/contexts/SettingsContext';
import { useWalletConnection } from '@/hooks/useWalletConnection';
import { useAuth } from '@/contexts/AuthContext';
import { useWallets } from '@/contexts/WalletContext';
import { useAdmin } from '@/contexts/AdminContext';
import { useToast } from '@/hooks/useToast';
import Modal from '@/components/ui/Modal';
import { formatPhoneBR, getPhoneDigits } from '@/utils/validation';
import { getStoredGooglePhone, setStoredGooglePhone } from '@/services/firebase';
import i18n from '@/i18n';
import brazilFlag from '@/assets/images/brazil.svg';
import usFlag from '@/assets/images/united_states.svg';

const ABSORB_STEPS = [0, 25, 50, 75, 100];

/** Reativar para exibir a opção "Solicitar pagamento da taxa em toda transação" nas Configurações. */
const SHOW_ASK_FEE_ON_RECEIVE_SETTING = false;

export default function SettingsScreen({ onBack }) {
  const {
    locale,
    displayCurrency,
    lockDisplayCurrency,
    askFeePaymentOnReceive,
    absorbTransferFee,
    convertAmountOnCurrencyChange,
    includeNameInWalletQR,
    includeEmailInWalletQR,
    includePhoneInWalletQR,
    includeNetworkInWalletQR,
    includePhotoInWalletQR,
    enableFirestoreNotifications,
    enableOnChainVerificationForNotifications,
    disableLegacyIncomingPolling,
    enableCreateNotificationOnSend,
    updateSetting,
  } = useSettings();

  const { t } = useTranslation();
  const { isCurrentUserAdmin } = useAdmin();
  const { walletAddress, selectedNetwork, connectedWallet, isPhantomEVM } = useWalletConnection();
  const { user } = useAuth();
  const { wallets, updateWallet, addWallet } = useWallets();
  const { success, error: showError } = useToast();

  const [showSettingsHelp, setShowSettingsHelp] = useState(false);
  const [showWalletQRModal, setShowWalletQRModal] = useState(false);
  /** Telefone editável para o QR da carteira (salvo na carteira ativa ao sair do campo) */
  const [walletQRPhone, setWalletQRPhone] = useState('');
  const lastSavedPhoneRef = useRef('');
  const [savingPhone, setSavingPhone] = useState(false);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const languageDropdownRef = useRef(null);
  const [currencyDropdownOpen, setCurrencyDropdownOpen] = useState(false);
  const currencyDropdownRef = useRef(null);
  /** Hint suspenso ao clicar em Ver ajuda (Moeda, Absorção, Campo Valor, etc.) */
  const [openHint, setOpenHint] = useState(null); // 'currency' | 'askFee' | 'absorb' | 'amountField' | null
  const hintAreaRef = useRef(null);

  const isWalletConnected =
    connectedWallet === 'metamask' ||
    connectedWallet === 'walletconnect' ||
    (connectedWallet === 'phantom' && isPhantomEVM);

  const activeWalletRecord = useMemo(() => {
    if (!walletAddress || !wallets?.length) return null;
    const addr = walletAddress.toLowerCase();
    return wallets.find((w) => w.address && w.address.toLowerCase() === addr) || null;
  }, [walletAddress, wallets]);

  // Telefone vinculado à conta do usuário (compartilhado entre todas as carteiras)
  const defaultPhone = getStoredGooglePhone(user?.uid) || user?.phoneNumber || '';
  useEffect(() => {
    const formatted = formatPhoneBR(defaultPhone);
    setWalletQRPhone(formatted);
    lastSavedPhoneRef.current = formatted;
  }, [user?.uid, defaultPhone]);

  useEffect(() => {
    if (!languageDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(e.target)) {
        setLanguageDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [languageDropdownOpen]);

  useEffect(() => {
    if (!currencyDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(e.target)) {
        setCurrencyDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [currencyDropdownOpen]);

  useEffect(() => {
    if (openHint == null) return;
    const handleClickOutside = (e) => {
      if (hintAreaRef.current && !hintAreaRef.current.contains(e.target)) {
        setOpenHint(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openHint]);

  const handlePhoneBlur = async () => {
    const trimmed = (walletQRPhone || '').trim();
    const toSave = trimmed ? formatPhoneBR(trimmed) : '';
    if (!user?.uid) return;
    if (getPhoneDigits(toSave) === getPhoneDigits(lastSavedPhoneRef.current || '')) return;

    setSavingPhone(true);
    try {
      setStoredGooglePhone(user.uid, toSave);
      lastSavedPhoneRef.current = toSave;
      success(t('settings.phoneSaved'));
    } catch {
      showError(t('settings.phoneSaveError'));
    } finally {
      setSavingPhone(false);
    }
  };

  const handlePhoneUndo = () => {
    setWalletQRPhone(lastSavedPhoneRef.current || '');
  };

  const phoneHasChange = getPhoneDigits(walletQRPhone) !== getPhoneDigits(lastSavedPhoneRef.current || '');

  const currentLocale = locale ?? i18n.language() ?? 'pt-BR';
  const handleLocaleChange = (e) => {
    const value = e.target.value;
    if (value !== 'pt-BR' && value !== 'en') return;
    updateSetting('locale', value);
    i18n.changeLanguage(value);
  };

  const walletQRPayload = useMemo(() => {
    if (!walletAddress) return null;
    const payload = {
      type: 'wallet',
      address: walletAddress,
    };
    const fromRecord = activeWalletRecord;
    const fromUser = user;
    if (includeNameInWalletQR) {
      payload.name = fromRecord?.name || fromUser?.name || fromUser?.displayName || '';
    }
    if (includeEmailInWalletQR) {
      payload.email = fromRecord?.email || fromUser?.email || '';
    }
    if (includePhoneInWalletQR) {
      payload.phone = (walletQRPhone || '').trim() || '';
    }
    if (includeNetworkInWalletQR && selectedNetwork) {
      payload.network = selectedNetwork.chainId;
      payload.networkName = selectedNetwork.name || '';
    }
    if (includePhotoInWalletQR && (fromUser?.photoURL || fromRecord?.photoURL)) {
      payload.photoURL = fromUser?.photoURL || fromRecord?.photoURL || '';
    }
    return payload;
  }, [
    walletAddress,
    activeWalletRecord,
    user,
    selectedNetwork,
    walletQRPhone,
    includeNameInWalletQR,
    includeEmailInWalletQR,
    includePhoneInWalletQR,
    includeNetworkInWalletQR,
    includePhotoInWalletQR,
  ]);

  const walletQRJson = walletQRPayload ? JSON.stringify(walletQRPayload) : '';

  const copyWalletQRJson = async () => {
    if (!walletQRJson) return;
    try {
      await navigator.clipboard.writeText(walletQRJson);
      success(t('settings.jsonCopied'));
    } catch {
      showError(t('settings.copyErrorSettings'));
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between gap-3 mb-8">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition"
            aria-label={t('settings.backAria')}
          >
            <ArrowLeft size={24} />
          </button>
          <Settings size={28} className="text-purple-400" />
          <h1 className="text-2xl font-bold text-white">{t('settings.title')}</h1>
        </div>
        <button
          type="button"
          onClick={() => setShowSettingsHelp(true)}
          className="w-7 h-7 flex items-center justify-center rounded-full border border-gray-500 text-gray-400 hover:text-white hover:border-white text-sm"
          aria-label={t('settings.helpAria')}
        >
          ?
        </button>
      </div>

      <div className="space-y-8">
        {/* Idioma e Moeda de Exibição — padrão: label acima, campo em caixa própria, lado a lado */}
        <section className={`bg-gray-800/50 backdrop-blur border border-purple-500/20 rounded-2xl p-6 ${languageDropdownOpen || currencyDropdownOpen || openHint ? 'relative z-[100]' : ''}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label className="text-gray-300 font-medium block mb-2">{t('settings.languageTitle')}</label>
              <div className="relative" ref={languageDropdownRef}>
                <button
                  type="button"
                  onClick={() => setLanguageDropdownOpen((v) => !v)}
                  className="w-full flex items-center gap-2 bg-gray-700 text-white rounded-lg p-3 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-purple-500 text-left"
                  aria-label={t('settings.languageTitle')}
                  aria-expanded={languageDropdownOpen}
                  aria-haspopup="listbox"
                >
                  <img
                    src={currentLocale === 'pt-BR' ? brazilFlag : usFlag}
                    alt=""
                    className="w-6 h-4 object-cover rounded shrink-0"
                    aria-hidden
                  />
                  <span>{currentLocale === 'pt-BR' ? t('settings.languageOptionPtBr') : t('settings.languageOptionEn')}</span>
                </button>
                {languageDropdownOpen && (
                  <ul
                    role="listbox"
                    className="absolute z-50 mt-1 w-full bg-gray-700 rounded-lg shadow-lg border border-gray-600 overflow-hidden"
                  >
                    {[
                      { value: 'pt-BR', flag: brazilFlag, labelKey: 'settings.languageOptionPtBr' },
                      { value: 'en', flag: usFlag, labelKey: 'settings.languageOptionEn' },
                    ].map((opt) => (
                      <li key={opt.value} role="option" aria-selected={currentLocale === opt.value}>
                        <button
                          type="button"
                          onClick={() => {
                            if (currentLocale !== opt.value) {
                              updateSetting('locale', opt.value);
                              i18n.changeLanguage(opt.value);
                            }
                            setLanguageDropdownOpen(false);
                          }}
                          className="w-full flex items-center gap-2 p-3 text-white hover:bg-gray-600 text-left"
                        >
                          <img src={opt.flag} alt="" className="w-6 h-4 object-cover rounded shrink-0" aria-hidden />
                          <span>{t(opt.labelKey)}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div ref={openHint === 'currency' ? hintAreaRef : null}>
              <div className="relative flex items-center gap-1.5 mb-2">
                <label className="text-gray-300 font-medium block">{t('settings.displayCurrencyTitle')}</label>
                <button
                  type="button"
                  onClick={() => setOpenHint((v) => (v === 'currency' ? null : 'currency'))}
                  className="w-5 h-5 flex items-center justify-center rounded-full border border-gray-500 text-gray-400 hover:text-white hover:border-white text-[11px] transition shrink-0"
                  aria-label={t('settings.displayCurrencyDescription')}
                  aria-expanded={openHint === 'currency'}
                >
                  ?
                </button>
                {openHint === 'currency' && (
                  <div className="absolute left-0 top-full mt-1 z-50 min-w-[200px] max-w-[280px] rounded-lg bg-gray-700 border border-gray-600 shadow-lg p-3 text-sm text-gray-300">
                    {t('settings.displayCurrencyDescription')}
                  </div>
                )}
              </div>
              <div
                  className="relative flex items-stretch rounded-lg border border-gray-600 bg-gray-700 focus-within:ring-2 focus-within:ring-purple-500 focus-within:border-transparent"
                  ref={currencyDropdownRef}
                >
                  <button
                    type="button"
                    onClick={() => setCurrencyDropdownOpen((v) => !v)}
                    className="flex-1 flex items-center gap-2 text-white p-3 focus:outline-none text-left min-w-0"
                    aria-label={t('settings.displayCurrencyTitle')}
                    aria-expanded={currencyDropdownOpen}
                    aria-haspopup="listbox"
                  >
                    <img
                      src={(displayCurrency || 'USD') === 'BRL' ? brazilFlag : usFlag}
                      alt=""
                      className="w-6 h-4 object-cover rounded shrink-0"
                      aria-hidden
                    />
                    <span>{(displayCurrency || 'USD') === 'BRL' ? t('settings.currencyOptionBRL') : t('settings.currencyOptionUSD')}</span>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); updateSetting('lockDisplayCurrency', !lockDisplayCurrency); }}
                    className="flex items-center justify-center w-12 bg-gray-700 text-gray-400 hover:text-white hover:bg-gray-600 transition shrink-0 border-l border-gray-600"
                    aria-label={lockDisplayCurrency ? t('settings.unlockDisplayCurrency') : t('settings.lockDisplayCurrency')}
                    title={lockDisplayCurrency ? t('settings.unlockDisplayCurrency') : t('settings.lockDisplayCurrency')}
                  >
                    {lockDisplayCurrency ? <Lock size={18} /> : <Unlock size={18} />}
                  </button>
                  {currencyDropdownOpen && (
                    <ul
                      role="listbox"
                      className="absolute z-50 mt-1 w-full bg-gray-700 rounded-lg shadow-lg border border-gray-600 overflow-hidden"
                    >
                      {[
                        { value: 'USD', flag: usFlag, labelKey: 'settings.currencyOptionUSD' },
                        { value: 'BRL', flag: brazilFlag, labelKey: 'settings.currencyOptionBRL' },
                      ].map((opt) => (
                        <li key={opt.value} role="option" aria-selected={(displayCurrency || 'USD') === opt.value}>
                          <button
                            type="button"
                            onClick={() => {
                              if ((displayCurrency || 'USD') !== opt.value) {
                                updateSetting('displayCurrency', opt.value);
                              }
                              setCurrencyDropdownOpen(false);
                            }}
                            className="w-full flex items-center gap-2 p-3 text-white hover:bg-gray-600 text-left"
                          >
                            <img src={opt.flag} alt="" className="w-6 h-4 object-cover rounded shrink-0" aria-hidden />
                            <span>{t(opt.labelKey)}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
            </div>
          </div>
        </section>

        {/* 1. Solicitar pagamento da taxa em toda transação (oculto; reativar com SHOW_ASK_FEE_ON_RECEIVE_SETTING) */}
        {SHOW_ASK_FEE_ON_RECEIVE_SETTING && (
          <section className={`bg-gray-800/50 backdrop-blur border border-purple-500/20 rounded-2xl p-6 ${openHint === 'askFee' ? 'relative z-[100]' : ''}`} ref={openHint === 'askFee' ? hintAreaRef : null}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 relative">
                <div className="flex items-center gap-1.5">
                  <label className="flex items-center gap-2 text-white font-medium cursor-pointer">
                    <input
                      type="checkbox"
                      checked={askFeePaymentOnReceive}
                      onChange={(e) => updateSetting('askFeePaymentOnReceive', e.target.checked)}
                      className="w-4 h-4 rounded border-gray-500 text-purple-500 focus:ring-purple-500"
                    />
                    {t('settings.askFeeOnReceive')}
                  </label>
                  <button
                    type="button"
                    onClick={() => setOpenHint((v) => (v === 'askFee' ? null : 'askFee'))}
                    className="w-5 h-5 flex items-center justify-center rounded-full border border-gray-500 text-gray-400 hover:text-white hover:border-white text-[11px] transition shrink-0"
                    aria-label={t('settings.askFeeHint')}
                    aria-expanded={openHint === 'askFee'}
                  >
                    ?
                  </button>
                </div>
                {openHint === 'askFee' && (
                  <div className="absolute left-0 top-full mt-1 z-50 min-w-[200px] max-w-[320px] rounded-lg bg-gray-700 border border-gray-600 shadow-lg p-3 text-sm text-gray-300">
                    {t('settings.askFeeHint')}
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* 2. Absorver taxa de transferência (true/false). Taxa de recebimento é sempre paga por quem cobra. */}
        <section className={`bg-gray-800/50 backdrop-blur border border-purple-500/20 rounded-2xl p-6 ${openHint === 'absorb' ? 'relative z-[100]' : ''}`} ref={openHint === 'absorb' ? hintAreaRef : null}>
          <div className="relative">
            <div className="relative flex items-center gap-1.5 mb-2">
              <label className="text-white font-medium block">
                {t('settings.absorbLabel')}
              </label>
              <button
                type="button"
                onClick={() => setOpenHint((v) => (v === 'absorb' ? null : 'absorb'))}
                className="w-5 h-5 flex items-center justify-center rounded-full border border-gray-500 text-gray-400 hover:text-white hover:border-white text-[11px] transition shrink-0"
                aria-label={t('settings.absorbHintLong')}
                aria-expanded={openHint === 'absorb'}
              >
                ?
              </button>
              {openHint === 'absorb' && (
                <div className="absolute left-0 top-full mt-1 z-50 min-w-[200px] max-w-[320px] rounded-lg bg-gray-700 border border-gray-600 shadow-lg p-3 text-sm text-gray-300">
                  {t('settings.absorbHintLong')}
                </div>
              )}
            </div>
            <label className="flex items-center gap-2 text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={absorbTransferFee}
                onChange={(e) => updateSetting('absorbTransferFee', e.target.checked)}
                className="w-4 h-4 rounded border-gray-500 text-purple-500 focus:ring-purple-500"
              />
              {t('settings.absorbTransferFeeOption')}
            </label>
          </div>
        </section>

        {/* 3. Campo Valor: ao trocar moeda de exibição */}
        <section className={`bg-gray-800/50 backdrop-blur border border-purple-500/20 rounded-2xl p-6 ${openHint === 'amountField' ? 'relative z-[100]' : ''}`} ref={openHint === 'amountField' ? hintAreaRef : null}>
          <div className="relative mb-3">
            <div className="flex items-center gap-1.5 mb-3">
              <h2 className="text-white font-medium block">{t('settings.fieldAmountLabel')}</h2>
              <button
                type="button"
                onClick={() => setOpenHint((v) => (v === 'amountField' ? null : 'amountField'))}
                className="w-5 h-5 flex items-center justify-center rounded-full border border-gray-500 text-gray-400 hover:text-white hover:border-white text-[11px] transition shrink-0"
                aria-label={t('settings.helpItemAmountField')}
                aria-expanded={openHint === 'amountField'}
              >
                ?
              </button>
            </div>
            {openHint === 'amountField' && (
              <div className="absolute left-0 top-full mt-1 z-50 min-w-[200px] max-w-[320px] rounded-lg bg-gray-700 border border-gray-600 shadow-lg p-3 text-sm text-gray-300">
                {t('settings.helpItemAmountField')}
              </div>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <label className="flex items-center gap-2 text-gray-300 cursor-pointer">
              <input
                type="radio"
                name="convertAmountOnCurrencyChange"
                checked={convertAmountOnCurrencyChange === true}
                onChange={() => updateSetting('convertAmountOnCurrencyChange', true)}
                className="w-4 h-4 border-gray-500 text-purple-500 focus:ring-purple-500"
              />
              {t('settings.convertByRate')}
            </label>
            <label className="flex items-center gap-2 text-gray-300 cursor-pointer">
              <input
                type="radio"
                name="convertAmountOnCurrencyChange"
                checked={convertAmountOnCurrencyChange === false}
                onChange={() => updateSetting('convertAmountOnCurrencyChange', false)}
                className="w-4 h-4 border-gray-500 text-purple-500 focus:ring-purple-500"
              />
              {t('settings.keepAmount')}
            </label>
          </div>
        </section>

        {/* 4. QR Code da carteira ativa — compartilhar como JSON (mesmo modelo da tela Receber) */}
        <section className="bg-gray-800/50 backdrop-blur border border-purple-500/20 rounded-2xl p-6">
          {!isWalletConnected ? (
            <>
              <h2 className="text-white font-medium flex items-center gap-2 mb-3">
                <QrCode size={20} className="text-purple-400" />
                {t('settings.walletQRTitle')}
              </h2>
              <p className="text-gray-400 text-sm">{t('settings.connectWalletForQR')}</p>
              <p className="text-gray-500 text-xs mt-1">{t('settings.youCanChangeOtherSettings')}</p>
            </>
          ) : (
            <div>
              <div className="flex gap-3 items-start mb-4">
                <div className="flex-1 min-w-0">
                  <h2 className="text-white font-medium flex items-center gap-2 mb-3">
                    <QrCode size={20} className="text-purple-400 shrink-0" />
                    {t('settings.walletQRTitle')}
                  </h2>
                  <p className="text-gray-400 text-sm break-words">
                    {t('settings.activeWallet')} <span className="font-mono text-purple-300">{walletAddress ? `${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}` : ''}</span>
                    {selectedNetwork?.name && (
                      <span className="text-gray-500 ml-2">· {selectedNetwork.name}</span>
                    )}
                  </p>
                </div>
                <div className="w-20 h-20 rounded-xl border-2 border-gray-600 bg-gray-800/50 overflow-hidden flex items-center justify-center shrink-0" aria-hidden="true">
                  {user?.photoURL ? (
                    <img src={user.photoURL} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-gray-500 text-xs">{t('settings.noPhoto')}</span>
                  )}
                </div>
              </div>
              <p className="text-gray-500 text-xs mb-3">{t('settings.selectQRFields')}</p>
              <div className="flex flex-col gap-2 mb-4">
                <label className="flex items-center gap-2 text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeNameInWalletQR}
                    onChange={(e) => updateSetting('includeNameInWalletQR', e.target.checked)}
                    className="w-4 h-4 rounded border-gray-500 text-purple-500 focus:ring-purple-500 shrink-0"
                  />
                  <span className="text-gray-400">{t('settings.nameLabel')}</span>
                  <span className="text-white truncate">{activeWalletRecord?.name || user?.name || user?.displayName || '—'}</span>
                </label>
                <label className="flex items-center gap-2 text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeEmailInWalletQR}
                    onChange={(e) => updateSetting('includeEmailInWalletQR', e.target.checked)}
                    className="w-4 h-4 rounded border-gray-500 text-purple-500 focus:ring-purple-500 shrink-0"
                  />
                  <span className="text-gray-400">{t('settings.emailLabel')}</span>
                  <span className="text-white truncate">{activeWalletRecord?.email || user?.email || '—'}</span>
                </label>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-1.5 text-gray-300 cursor-pointer shrink-0">
                    <input
                      type="checkbox"
                      checked={includePhoneInWalletQR}
                      onChange={(e) => updateSetting('includePhoneInWalletQR', e.target.checked)}
                      className="w-4 h-4 rounded border-gray-500 text-purple-500 focus:ring-purple-500"
                    />
                    <span className="text-sm">{t('settings.phoneLabel')}</span>
                  </label>
                  <div className="flex-1 min-w-0 flex items-center rounded-lg border border-gray-600 bg-gray-700/50 focus-within:ring-1 focus-within:ring-purple-500 focus-within:border-transparent overflow-hidden relative">
                    <input
                      type="tel"
                      inputMode="numeric"
                      value={walletQRPhone}
                      onChange={(e) => setWalletQRPhone(formatPhoneBR(e.target.value))}
                      onBlur={handlePhoneBlur}
                      disabled={savingPhone}
                      placeholder={t('settings.phonePlaceholder')}
                      className="flex-1 min-w-0 pl-3 pr-24 py-1.5 bg-transparent text-white placeholder-gray-500 text-sm focus:outline-none disabled:opacity-70"
                    />
                    {!phoneHasChange && (
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 whitespace-nowrap pointer-events-none">
                        {t('settings.savedOnBlur')}
                      </span>
                    )}
                    {phoneHasChange && (
                      <button
                        type="button"
                        tabIndex={-1}
                        onClick={handlePhoneUndo}
                        className="shrink-0 px-2 py-1.5 text-xs text-purple-300 hover:text-purple-200 hover:bg-white/5 transition"
                      >
                        {t('common.undo')}
                      </button>
                    )}
                  </div>
                </div>
                <label className="flex items-center gap-2 text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeNetworkInWalletQR}
                    onChange={(e) => updateSetting('includeNetworkInWalletQR', e.target.checked)}
                    className="w-4 h-4 rounded border-gray-500 text-purple-500 focus:ring-purple-500"
                  />
                  {t('settings.networkChainLabel')}
                </label>
                <label className="flex items-center gap-2 text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includePhotoInWalletQR}
                    onChange={(e) => updateSetting('includePhotoInWalletQR', e.target.checked)}
                    className="w-4 h-4 rounded border-gray-500 text-purple-500 focus:ring-purple-500"
                  />
                  {t('settings.photoLabel')}
                </label>
              </div>
              <button
                type="button"
                onClick={() => setShowWalletQRModal(true)}
                className="flex items-center justify-center gap-2 w-full py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-sm font-medium transition"
              >
                <QrCode size={18} />
                {t('settings.generateQR')}
              </button>
            </div>
          )}
        </section>

        {/* 5. Flags temporárias de notificações (apenas para admins) */}
        {isCurrentUserAdmin && (
          <section className="bg-gray-900/60 backdrop-blur border border-amber-500/30 rounded-2xl p-6">
            <h2 className="text-white font-medium block mb-2">
              {t('settings.adminNotificationsTitle', 'Configurações administrativas de notificações')}
            </h2>
            <p className="text-gray-400 text-xs mb-4">
              {t(
                'settings.adminNotificationsDescription',
                'Flags temporárias para ligar/desligar partes do novo sistema de notificações. Use apenas para depuração em caso de problemas.',
              )}
            </p>
            <div className="space-y-3">
              <label className="flex items-start gap-2 text-gray-200 cursor-pointer">
                <input
                  type="checkbox"
                  checked={enableFirestoreNotifications}
                  onChange={(e) => updateSetting('enableFirestoreNotifications', e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-gray-500 text-purple-500 focus:ring-purple-500"
                />
                <div>
                  <div className="text-sm font-medium">
                    {t(
                      'settings.flagEnableFirestoreNotifications',
                      'Ativar sistema de notificações via Firestore',
                    )}
                  </div>
                  <p className="text-xs text-gray-400">
                    {t(
                      'settings.flagEnableFirestoreNotificationsHint',
                      'Se desativado, o listener de notificações Firestore será totalmente desligado (como se o novo sistema não existisse).',
                    )}
                  </p>
                </div>
              </label>

              <label className="flex items-start gap-2 text-gray-200 cursor-pointer opacity-90">
                <input
                  type="checkbox"
                  checked={enableOnChainVerificationForNotifications}
                  onChange={(e) =>
                    updateSetting('enableOnChainVerificationForNotifications', e.target.checked)
                  }
                  className="mt-0.5 w-4 h-4 rounded border-gray-500 text-purple-500 focus:ring-purple-500"
                  disabled={!enableFirestoreNotifications}
                />
                <div>
                  <div className="text-sm font-medium">
                    {t(
                      'settings.flagEnableOnChainVerification',
                      'Ativar verificação on-chain das notificações',
                    )}
                  </div>
                  <p className="text-xs text-gray-400">
                    {t(
                      'settings.flagEnableOnChainVerificationHint',
                      'Quando ligado, cada notificação pendente é confirmada na blockchain antes de ser exibida.',
                    )}
                  </p>
                </div>
              </label>

              <label className="flex items-start gap-2 text-gray-200 cursor-pointer opacity-90">
                <input
                  type="checkbox"
                  checked={enableCreateNotificationOnSend}
                  onChange={(e) => updateSetting('enableCreateNotificationOnSend', e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-gray-500 text-purple-500 focus:ring-purple-500"
                />
                <div>
                  <div className="text-sm font-medium">
                    {t(
                      'settings.flagEnableCreateNotificationOnSend',
                      'Criar notificação no Firestore ao enviar (tx confirmada)',
                    )}
                  </div>
                  <p className="text-xs text-gray-400">
                    {t(
                      'settings.flagEnableCreateNotificationOnSendHint',
                      'Quando desligado, nenhuma nova notificação será criada após o envio, mesmo com o listener Firestore ativo.',
                    )}
                  </p>
                </div>
              </label>

              <label className="flex items-start gap-2 text-gray-200 cursor-pointer opacity-90">
                <input
                  type="checkbox"
                  checked={disableLegacyIncomingPolling}
                  onChange={(e) => updateSetting('disableLegacyIncomingPolling', e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-gray-500 text-purple-500 focus:ring-purple-500"
                />
                <div>
                  <div className="text-sm font-medium">
                    {t(
                      'settings.flagDisableLegacyPolling',
                      'Desativar polling legado de recebimento',
                    )}
                  </div>
                  <p className="text-xs text-gray-400">
                    {t(
                      'settings.flagDisableLegacyPollingHint',
                      'Quando ligado, o polling antigo de recebimento na blockchain é desligado e apenas o sistema de notificações via Firestore é usado.',
                    )}
                  </p>
                </div>
              </label>
            </div>
          </section>
        )}
      </div>

      {/* Modal: QR Code da carteira (JSON, mesmo modelo da tela Receber) */}
      <Modal
        isOpen={showWalletQRModal && !!walletQRJson}
        onClose={() => setShowWalletQRModal(false)}
        title={t('settings.walletQRModalTitle')}
      >
        <div className="flex flex-col items-center gap-4">
          <p className="text-gray-400 text-sm text-center">
            {t('settings.walletQRModalDesc')}
          </p>
          <div className="p-4 bg-white rounded-xl">
            <QRCodeSVG value={walletQRJson} size={200} level="M" />
          </div>
          <div className="w-full flex gap-2">
            <button
              type="button"
              onClick={copyWalletQRJson}
              className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-sm"
            >
              <Copy size={16} />
              {t('settings.copyJSON')}
            </button>
            <button
              type="button"
              onClick={() => setShowWalletQRModal(false)}
              className="px-4 py-2.5 border border-gray-600 text-gray-300 hover:bg-gray-700 rounded-xl text-sm"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Ajuda geral das Configurações */}
      <Modal
        isOpen={showSettingsHelp}
        onClose={() => setShowSettingsHelp(false)}
        title={t('settings.helpTitle')}
      >
        <div className="space-y-3 text-sm text-gray-200">
          <p>{t('settings.helpIntro')}</p>
          <ul className="list-disc list-inside space-y-1 text-gray-300">
            <li>{t('settings.helpItemLanguage')}</li>
            <li>{t('settings.helpItemAskFee')}</li>
            <li>{t('settings.helpItemAbsorb')}</li>
            <li>{t('settings.helpItemAmountField')}</li>
            <li>{t('settings.helpItemWalletQR')}</li>
          </ul>
          <p className="text-xs text-gray-400">
            {t('settings.helpTip')}
          </p>
        </div>
      </Modal>
    </div>
  );
}
